/**
 * AI Agent Endpoint Form Page - Create/Edit AI agent endpoints.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Skeleton,
  Switch,
  FormControlLabel,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  Save,
  AltRoute,
  Settings,
  Hub,
  Cloud,
  ContentCopy,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import ButtonTabs, { type ButtonTab } from '../components/ButtonTabs';
import DetailHeader from '../components/DetailHeader';
import TenantResourceCopyDialog from '../components/TenantResourceCopyDialog';
import type {
  AIAgentEndpoint,
  AIAgentEndpointType,
  AIAgentProvider,
  Agent,
} from '../types';
import usePageTitle from '../hooks/usePageTitle';
import { useAuth } from '../contexts/AuthContext';

interface SipTrunkPool {
  id: string;
  name: string;
  isActive: boolean;
  maxConcurrentCalls?: number | null;
}

type AiAgentEndpointTab = 'basic' | 'runtime';

export default function AiAgentEndpointFormPage() {
  const { t } = useTranslation(['pages', 'common']);
  const navigate = useNavigate();
  const { user, currentTenant, isSuperuser } = useAuth();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [providers, setProviders] = useState<AIAgentProvider[]>([]);
  const [aiAgents, setAiAgents] = useState<Agent[]>([]);
  const [sipTrunkPools, setSipTrunkPools] = useState<SipTrunkPool[]>([]);
  const [loadingAiAgents, setLoadingAiAgents] = useState(false);
  const [existingEndpoint, setExistingEndpoint] = useState<AIAgentEndpoint | null>(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copySubmitting, setCopySubmitting] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    identifier: '',
    aiAgentProviderId: '',
    aiAgentId: '',
    endpointType: 'eleven_phone_number' as AIAgentEndpointType,
    providerEndpointId: '',
    isActive: true,
    isPrimary: false,
    sipTrunkPoolId: '',
  });

  const endpointDescriptor = existingEndpoint?.name || formData.name || formData.identifier;
  usePageTitle(t('pages:aiAgentEndpoints.title'), endpointDescriptor);
  const canCopyResource = Boolean(
    isEditing
      && existingEndpoint?.id
      && isSuperuser
      && user?.tenantId
      && currentTenant?.id
      && currentTenant.id === user.tenantId,
  );

  const selectedProvider = providers.find((provider) => String(provider.id) === formData.aiAgentProviderId) || null;
  const selectedAiAgent = aiAgents.find((aiAgent) => String(aiAgent.id) === formData.aiAgentId) || null;
  const selectedSipTrunkPool = sipTrunkPools.find((pool) => String(pool.id) === formData.sipTrunkPoolId) || null;

  const endpointTabs = useMemo<ButtonTab[]>(
    () => [
      {
        id: 'basic',
        label: t('pages:aiAgentEndpoints.tabs.basicInfo'),
        tabProps: {
          icon: <Settings sx={{ fontSize: 18 }} />,
          iconPosition: 'start',
        },
      },
      {
        id: 'runtime',
        label: t('pages:aiAgentEndpoints.tabs.runtime'),
        tabProps: {
          icon: <Hub sx={{ fontSize: 18 }} />,
          iconPosition: 'start',
        },
      },
    ],
    [t],
  );

  const endpointTypeOptions = useMemo(
    () => [
      { value: 'eleven_phone_number', label: t('pages:aiAgentEndpoints.types.elevenPhoneNumber') },
      { value: 'sip_uri', label: t('pages:aiAgentEndpoints.types.sipUri') },
      { value: 'extension', label: t('pages:aiAgentEndpoints.types.extension') },
      { value: 'queue', label: t('pages:aiAgentEndpoints.types.queue') },
    ],
    [t],
  );

  const tabFromUrl = searchParams.get('tab') as AiAgentEndpointTab | null;
  const normalizedTab = endpointTabs.some((tab) => tab.id === tabFromUrl)
    ? (tabFromUrl as AiAgentEndpointTab)
    : 'basic';

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const currentTab = params.get('tab');
    if (normalizedTab === 'basic' && currentTab) {
      params.delete('tab');
      setSearchParams(params, { replace: true });
      return;
    }
    if (normalizedTab !== 'basic' && currentTab !== normalizedTab) {
      params.set('tab', normalizedTab);
      setSearchParams(params, { replace: true });
    }
  }, [normalizedTab, searchParams, setSearchParams]);

  const fetchInitialDataRef = useRef<() => Promise<void>>(async () => undefined);
  const fetchAiAgentsRef = useRef<(providerId: string) => Promise<void>>(
    async () => undefined,
  );

  useEffect(() => {
    void fetchInitialDataRef.current();
  }, [id]);

  useEffect(() => {
    if (!formData.aiAgentProviderId) {
      setAiAgents([]);
      return;
    }
    void fetchAiAgentsRef.current(formData.aiAgentProviderId);
  }, [formData.aiAgentProviderId]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [providersResponse, sipTrunkPoolsResponse] = await Promise.all([
        api.get('/api/ai-agents-providers/available/'),
        api.get('/api/sip-trunks/pools/available/'),
      ]);

      const providerData = providersResponse.data.results ?? providersResponse.data ?? [];
      setProviders(
        providerData.filter((provider: AIAgentProvider) => provider.supportsAgents),
      );

      const poolData = sipTrunkPoolsResponse.data.results ?? sipTrunkPoolsResponse.data ?? [];
      const activePools = Array.isArray(poolData)
        ? poolData.filter((pool: SipTrunkPool) => pool.isActive)
        : [];
      setSipTrunkPools(activePools);
      if (activePools.length === 1) {
        setFormData((prev) => ({ ...prev, sipTrunkPoolId: prev.sipTrunkPoolId || activePools[0].id }));
      }

      if (id) {
        const endpointResponse = await api.get(`/api/ai-agent-endpoints/${id}/`);
        const endpoint: AIAgentEndpoint = endpointResponse.data;
        setExistingEndpoint(endpoint);
        setFormData({
          name: endpoint.name,
          identifier: endpoint.identifier,
          aiAgentProviderId: String(endpoint.aiAgentProviderId || ''),
          aiAgentId: String(endpoint.aiAgentId || ''),
          endpointType: endpoint.endpointType,
          providerEndpointId: endpoint.providerEndpointId || '',
          isActive: endpoint.isActive,
          isPrimary: endpoint.isPrimary,
          sipTrunkPoolId: String(endpoint.sipTrunkPoolId || ''),
        });
      }
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { detail?: string } } };
      console.error('Error loading AI agent endpoint data:', err);
      setError(apiError?.response?.data?.detail || t('pages:aiAgentEndpoints.errors.loading'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAiAgents = async (aiAgentProviderId: string) => {
    setLoadingAiAgents(true);
    try {
      const response = await api.get('/api/agents/list_simple/', {
        params: { ai_agent_provider: aiAgentProviderId },
      });
      const data = response.data.results ?? response.data ?? [];
      const normalizedAgents = Array.isArray(data) ? data : [];
      if (
        existingEndpoint
        && String(existingEndpoint.aiAgentProviderId) === aiAgentProviderId
        && existingEndpoint.aiAgentId
        && !normalizedAgents.some((aiAgent: Agent) => String(aiAgent.id) === String(existingEndpoint.aiAgentId))
      ) {
        normalizedAgents.push({
          id: existingEndpoint.aiAgentId,
          name: existingEndpoint.aiAgentName,
          agentType: 'cloud',
          agentTypeDisplay: '',
          aiAgentProviderId: existingEndpoint.aiAgentProviderId,
          aiAgentProviderName: existingEndpoint.aiAgentProviderName,
          providerAgentId: '',
          transferTargetRequired: false,
          isActive: existingEndpoint.isActive,
        } as Agent);
      }
      setAiAgents(normalizedAgents);
    } catch (err) {
      console.error('Error loading AI agents for endpoint form:', err);
      setAiAgents([]);
    } finally {
      setLoadingAiAgents(false);
    }
  };

  fetchInitialDataRef.current = fetchInitialData;
  fetchAiAgentsRef.current = fetchAiAgents;

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const payload = {
        name: formData.name,
        identifier: formData.identifier,
        ai_agent_provider_id: formData.aiAgentProviderId,
        ai_agent_id: formData.aiAgentId,
        endpoint_type: formData.endpointType,
        provider_endpoint_id: formData.providerEndpointId,
        is_active: formData.isActive,
        is_primary: formData.isPrimary,
        sip_trunk_pool_id: formData.sipTrunkPoolId,
      };

      if (isEditing) {
        await api.patch(`/api/ai-agent-endpoints/${id}/`, payload);
        setSuccess(t('pages:aiAgentEndpoints.updateSuccess'));
      } else {
        await api.post('/api/ai-agent-endpoints/', payload);
        setSuccess(t('pages:aiAgentEndpoints.createSuccess'));
        setTimeout(() => navigate('/ai-agent-endpoints'), 1500);
      }
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { detail?: string; error?: string } } };
      console.error('Error saving AI agent endpoint:', err);
      setError(
        apiError?.response?.data?.detail
          || apiError?.response?.data?.error
          || t('pages:aiAgentEndpoints.errors.saving'),
      );
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = () => {
    return Boolean(
      formData.name
      && formData.identifier
      && formData.aiAgentProviderId
      && formData.aiAgentId
      && formData.endpointType
      && formData.sipTrunkPoolId,
    );
  };

  const getProviderIcon = () => {
    return <Cloud />;
  };

  const headerTitle = isEditing
    ? existingEndpoint?.name || t('pages:aiAgentEndpoints.editTitle')
    : t('pages:aiAgentEndpoints.newTitle');

  const breadcrumbs = [
    { label: t('common:menu.aiAgentEndpoints'), to: '/ai-agent-endpoints' },
    { label: isEditing ? t('pages:aiAgentEndpoints.edit') : t('pages:aiAgentEndpoints.new') },
  ];

  const headerActions = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {canCopyResource && (
        <Button
          variant="outlined"
          startIcon={<ContentCopy />}
          onClick={() => {
            setCopyError(null);
            setCopyDialogOpen(true);
          }}
          disabled={saving || copySubmitting}
        >
          {t('pages:aiAgentEndpoints.copy.button')}
        </Button>
      )}
      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
        onClick={handleSubmit}
        disabled={saving || !canSubmit()}
      >
        {saving ? t('common:actions.saving') : t('common:actions.save')}
      </Button>
    </Box>
  );

  const handleCopyToTenant = async (targetTenantId: string) => {
    if (!id) {
      return;
    }
    setCopySubmitting(true);
    setCopyError(null);
    try {
      const response = await api.post(`/api/ai-agent-endpoints/${id}/copy_to_tenant/`, {
        targetTenantId,
      });
      setCopyDialogOpen(false);
      setSuccess(
        response.data.endpointReused
          ? t('pages:aiAgentEndpoints.copy.successReused', {
            targetTenantName: response.data.targetTenantName,
            name: response.data.endpoint?.name || existingEndpoint?.name || formData.name,
          })
          : t('pages:aiAgentEndpoints.copy.successCreated', {
            targetTenantName: response.data.targetTenantName,
            name: response.data.endpoint?.name || existingEndpoint?.name || formData.name,
          }),
      );
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { detail?: string; error?: string } } };
      setCopyError(
        apiError?.response?.data?.detail
          || apiError?.response?.data?.error
          || t('pages:aiAgentEndpoints.copy.error'),
      );
    } finally {
      setCopySubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      <DetailHeader
        title={headerTitle}
        sectionIcon={<AltRoute sx={{ fontSize: 32, color: 'primary.main' }} />}
        entityId={existingEndpoint?.id ?? null}
        subtitle={t('pages:aiAgentEndpoints.subtitle')}
        breadcrumbs={breadcrumbs}
        actions={headerActions}
        backHref="/ai-agent-endpoints"
      />

      <TenantResourceCopyDialog
        open={copyDialogOpen}
        title={t('pages:aiAgentEndpoints.copy.title')}
        description={t('pages:aiAgentEndpoints.copy.description')}
        submitting={copySubmitting}
        error={copyError}
        sourceTenantId={user?.tenantId}
        onClose={() => {
          if (copySubmitting) {
            return;
          }
          setCopyDialogOpen(false);
          setCopyError(null);
        }}
        onSubmit={handleCopyToTenant}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <ButtonTabs
        tabs={endpointTabs}
        defaultTab="basic"
        tabParam="tab"
        useUrl
        renderContent={false}
      />

      {normalizedTab === 'basic' && (
        <Card>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 860 }}>
            <FormControl fullWidth required disabled={saving}>
              <InputLabel>{t('pages:aiAgentEndpoints.provider')}</InputLabel>
              <Select
                value={formData.aiAgentProviderId}
                label={t('pages:aiAgentEndpoints.provider')}
                onChange={(event) => {
                  setFormData((prev) => ({
                    ...prev,
                    aiAgentProviderId: event.target.value,
                    aiAgentId: '',
                  }));
                }}
              >
                {providers.map((provider) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getProviderIcon()}
                      <span>{provider.name}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete<Agent, false, false, false>
              fullWidth
              options={aiAgents}
              loading={loadingAiAgents}
              value={selectedAiAgent}
              onChange={(_, nextAiAgent) => {
                setFormData((prev) => ({ ...prev, aiAgentId: nextAiAgent?.id || '' }));
              }}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('pages:aiAgentEndpoints.aiAgent')}
                  required
                  helperText={t('pages:aiAgentEndpoints.aiAgentHelper')}
                />
              )}
              noOptionsText={t('pages:aiAgentEndpoints.noAiAgents')}
              disabled={!formData.aiAgentProviderId || saving}
            />

            <FormControl fullWidth required disabled={saving}>
              <InputLabel>{t('pages:aiAgentEndpoints.endpointType')}</InputLabel>
              <Select
                value={formData.endpointType}
                label={t('pages:aiAgentEndpoints.endpointType')}
                onChange={(event) => {
                  setFormData((prev) => ({
                    ...prev,
                    endpointType: event.target.value as AIAgentEndpointType,
                  }));
                }}
              >
                {endpointTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('pages:aiAgentEndpoints.name')}
              value={formData.name}
              onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
              fullWidth
              required
              disabled={saving}
            />

            <TextField
              label={t('pages:aiAgentEndpoints.identifier')}
              value={formData.identifier}
              onChange={(event) => setFormData((prev) => ({ ...prev, identifier: event.target.value }))}
              fullWidth
              required
              helperText={t('pages:aiAgentEndpoints.identifierHelper')}
              disabled={saving}
            />

            <TextField
              label={t('pages:aiAgentEndpoints.providerEndpointId')}
              value={formData.providerEndpointId}
              onChange={(event) => setFormData((prev) => ({ ...prev, providerEndpointId: event.target.value }))}
              fullWidth
              helperText={t('pages:aiAgentEndpoints.providerEndpointIdHelper')}
              disabled={saving}
            />

            {selectedProvider && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={getProviderIcon()}
                  label={selectedProvider.name}
                  color="primary"
                  size="small"
                />
                {selectedAiAgent && (
                  <Chip
                    label={selectedAiAgent.name}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {normalizedTab === 'runtime' && (
        <Card>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 860 }}>
            <Alert severity="info">
              {t('pages:aiAgentEndpoints.runtimeHelp')}
            </Alert>

            <FormControl fullWidth required disabled={saving}>
              <InputLabel>{t('pages:aiAgentEndpoints.sipTrunkPool')}</InputLabel>
              <Select
                value={formData.sipTrunkPoolId}
                label={t('pages:aiAgentEndpoints.sipTrunkPool')}
                onChange={(event) => {
                  setFormData((prev) => ({ ...prev, sipTrunkPoolId: event.target.value }));
                }}
              >
                {sipTrunkPools.length === 0 ? (
                  <MenuItem disabled>{t('pages:aiAgentEndpoints.noSipTrunkPools')}</MenuItem>
                ) : (
                  sipTrunkPools.map((pool) => (
                    <MenuItem key={pool.id} value={pool.id}>
                      {pool.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {selectedSipTrunkPool && (
              <Alert severity="success">
                {t('pages:aiAgentEndpoints.runtimeSummary', {
                  sipTrunkPoolName: selectedSipTrunkPool.name,
                })}
              </Alert>
            )}

            <FormControlLabel
              control={(
                <Switch
                  checked={formData.isActive}
                  onChange={(event) => setFormData((prev) => ({ ...prev, isActive: event.target.checked }))}
                />
              )}
              label={t('pages:aiAgentEndpoints.isActive')}
            />

            <FormControlLabel
              control={(
                <Switch
                  checked={formData.isPrimary}
                  onChange={(event) => setFormData((prev) => ({ ...prev, isPrimary: event.target.checked }))}
                />
              )}
              label={t('pages:aiAgentEndpoints.isPrimary')}
            />

            {existingEndpoint?.lastSyncedAt && (
              <Typography variant="caption" color="text.secondary">
                {t('pages:aiAgentEndpoints.lastSyncedAt', { value: existingEndpoint.lastSyncedAt })}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
